import { Box, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  ModalActionButtons,
  Select,
  Space
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";

import ClassPostFragment from "../../../shared/client/queries/ClassPostFragment";
import {
  GetClassPostsBySectionForCopyClassPostModalQueryQuery,
  GetSectionsForCopyClassPostModalQueryQuery
} from "../../../shared/generated/graphql";
import * as Section from "../../../shared/Section";

// Get the query for the retrieving all sections in the given activity
const sectionsByActivityQuery = gql`
  query GetSectionsForCopyClassPostModalQuery($activityUid: ID!) {
    activity(uid: $activityUid) {
      uid
      sections {
        uid
        start_time
        end_time
        deleted_at
        canceled_at
        published_at
        ongoing_stopped_at
      }
    }
  }
`;

// Get the query for the retrieving all sections in the given activity
const classPostsBySectionQuery = gql`
  query GetClassPostsBySectionForCopyClassPostModalQuery(
    $sectionUid: ID!
    $plainText: Boolean
  ) {
    section(uid: $sectionUid) {
      uid
      posts {
        ...ClassPostFragment
      }
      unpublishedPosts {
        ...ClassPostFragment
      }
    }
  }
  ${ClassPostFragment}
`;

function formatPost({ post, userTimeZone }: $TSFixMe) {
  // Format post based on the day, time, and beginning of the post.
  if (post && post.publishAt) {
    const { attachments, video } = post.messageContent;
    let postText = `${Time.formatDateTimeWithWeekday(
      post.publishAt,
      userTimeZone
    )}: ${post.messageContent.text ? post.messageContent.text : ""}`;
    if (attachments.length) {
      postText += ` ${attachments.length} attachments`;
    }
    if (video) {
      postText += ` 1 video`;
    }
    return postText;
  }
  return "";
}

export function CopyClassPostModal({
  onCancel,
  handleSubmit,
  activity,
  section
}: $TSFixMe) {
  const [selectedSectionUid, setSelectedSectionUid] = React.useState(
    section && section.uid
  );
  const [selectedPost, setSelectedPost] = React.useState(null);
  const trackEvent = useTrackEvent();

  const { t } = useTranslation(
    "client\\components\\classroom\\CopyClassPostModal"
  );
  const { loading, data, error } =
    useQueryWithPreviousData<GetSectionsForCopyClassPostModalQueryQuery>(
      sectionsByActivityQuery,
      {
        fetchPolicy: "cache-and-network",
        variables: { activityUid: activity.uid }
      }
    );

  const { loading: postsLoading, data: posts } =
    useQueryWithPreviousData<GetClassPostsBySectionForCopyClassPostModalQueryQuery>(
      classPostsBySectionQuery,
      {
        fetchPolicy: "cache-and-network",
        variables: {
          sectionUid: selectedSectionUid,
          plainText: true
        },
        skip: !selectedSectionUid
      }
    );

  if (error) {
    console.error(
      "Sorry there was an error right now! You can try again later"
    );
  }

  const { currentUser, currentUserIsLoading, userTimeZone } = useSession();

  const allPostsBySection = React.useMemo(() => {
    if (!posts) {
      return [];
    }
    return _.orderBy(
      // @ts-ignore TS(2531): Object is possibly 'null'.
      posts.section.posts.concat(posts.section.unpublishedPosts),
      "publishAt",
      "desc"
    );
  }, [posts]);

  const postOptions = React.useMemo(
    () =>
      allPostsBySection.map(post => ({
        label: _.truncate(formatPost({ post, userTimeZone }), {
          length: 60,
          separator: /,? +/
        }),
        value: post.uid
      })),
    [allPostsBySection, userTimeZone]
  );

  if (loading || currentUserIsLoading) {
    return null;
  }

  // @ts-ignore TS(2532): Object is possibly 'undefined'.
  const { sections } = data.activity;

  // Make sure the sections displayed are not drafts
  const sortedSections = _.orderBy(
    sections.filter(s => Section.isScheduled(s)),
    "start_time",
    "desc"
  );

  const handlePostChange = (value: string) => {
    const filteredPost =
      allPostsBySection.find(post => post.uid === value) ?? null;
    // @ts-ignore TS(2345): Argument of type '{ __typename?: "ClassPost" | und... Remove this comment to see the full error message
    setSelectedPost(filteredPost);
  };

  const handleSectionChange = (value: string) => {
    setSelectedSectionUid(value);
    setSelectedPost(null);
  };

  const sectionOptions = sortedSections.map(s => ({
    label: _.truncate(
      // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      Time.formatDateTimeWithWeekday(s.start_time, userTimeZone),
      {
        length: 60,
        separator: /,? +/
      }
    ),
    value: s.uid
  }));

  return (
    <Box>
      <Typography variant="h2">{t("Copy an existing post")}</Typography>
      <Box>
        {t("You may copy a post from other sections of the same class.")}
      </Box>
      <Space y="medium" />
      <Typography
        variant="h3"
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {t("Choose a section")}
      </Typography>
      <Select
        value={selectedSectionUid ? selectedSectionUid : ""}
        options={sectionOptions}
        onChange={handleSectionChange}
      />
      <Space y="medium" />
      <Typography
        variant="h3"
        sx={{
          marginBottom: "0.5em"
        }}
      >
        {t("Choose a post")}
      </Typography>
      {postOptions.length > 0 ? (
        <Select
          style={{ width: "100%" }}
          placeholder={t("Select a post")}
          // @ts-ignore TS(2339): Property 'uid' does not exist on type 'never'.
          value={selectedPost ? selectedPost.uid : ""}
          options={postOptions}
          onChange={handlePostChange}
        />
      ) : postsLoading ? (
        t("Loading...")
      ) : (
        t("No posts in selected section")
      )}
      {selectedPost && (
        <Box
          sx={{
            marginTop: "1em"
          }}
        >
          <Box
            sx={{
              fontWeight: "fontWeightBold"
            }}
          >
            {t("Post preview:")}
          </Box>
          {/* @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message */}
          {selectedPost.messageContent && (
            <Box>
              <Box>
                {/* @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message */}
                {_.truncate(selectedPost.messageContent.text, {
                  length: 80,
                  separator: /,? +/
                })}
              </Box>
              <Box>
                {/* @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message */}
                {selectedPost.messageContent.attachments.length > 0 &&
                  t("{{numAttachments}} attachment", {
                    numAttachments:
                      // @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message
                      selectedPost.messageContent.attachments.length,
                    // @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message
                    count: selectedPost.messageContent.attachments.length,
                    defaultValue_plural: "{{numAttachments}} attachments"
                  })}
              </Box>
              {/* @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message */}
              <Box>{selectedPost.messageContent.video && t`1 video`}</Box>
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          fontWeight: "fontWeightBold",
          marginY: "1em"
        }}
      >
        {t("You will be able to edit before publishing.")}
      </Box>
      <ModalActionButtons
        primaryLabel={t("Copy this post")}
        cancelLabel={t("Cancel")}
        onPrimary={() => {
          trackEvent("copy_section_post_confirm_touch", {
            sectionUid: section.uid,
            activityUid: activity.uid,
            // @ts-ignore TS(2531): Object is possibly 'null'.
            userUid: currentUser.uid,
            // @ts-ignore TS(2339): Property 'uid' does not exist on type 'never'.
            postUid: selectedPost && selectedPost.uid
          });
          // @ts-ignore TS(2339): Property 'messageContent' does not exist on type '... Remove this comment to see the full error message
          handleSubmit((selectedPost && selectedPost.messageContent) || "");
        }}
        onCancel={() => {
          onCancel();
          trackEvent("copy_section_post_cancel_touch", {
            sectionUid: section.uid,
            activityUid: activity.uid,
            // @ts-ignore TS(2531): Object is possibly 'null'.
            userUid: currentUser.uid,
            // @ts-ignore TS(2339): Property 'uid' does not exist on type 'never'.
            postUid: selectedPost && selectedPost.uid
          });
        }}
      />
    </Box>
  );
}
